/** Dialogue stays available until dismissed; spoken use is captured by the transcript. */
import { useRef, useState } from 'react';
import type { NpcLineCue } from '@/api/types';
import { userFacingError } from '@/api/client';

export function NpcLineCards({
  cues,
  onResolve,
}: {
  cues: NpcLineCue[];
  onResolve: (cue: NpcLineCue, status: 'used' | 'dismissed') => Promise<void>;
}) {
  if (cues.length === 0) return null;
  return (
    <section className="npc-lines" aria-label="Suggested NPC responses">
      <header className="npc-lines__header">
        <span className="desk-eyebrow">NPC dialogue</span>
        <span className="npc-lines__count" role="status">
          {cues.length} ready to use
        </span>
      </header>
      {cues.map((cue) => (
        <LineCard key={cue.id} cue={cue} onResolve={onResolve} />
      ))}
    </section>
  );
}

function LineCard({
  cue,
  onResolve,
}: {
  cue: NpcLineCue;
  onResolve: (cue: NpcLineCue, status: 'used' | 'dismissed') => Promise<void>;
}) {
  const [working, setWorking] = useState(false);
  const pending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  async function resolve(status: 'used' | 'dismissed') {
    if (pending.current) return;
    pending.current = true;
    setWorking(true);
    setError(null);
    try {
      await onResolve(cue, status);
    } catch (err) {
      setError(
        userFacingError(
          err,
          'That response wasn’t saved. Your line is still here; try again.',
        ),
      );
    } finally {
      pending.current = false;
      setWorking(false);
    }
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(cue.line);
      setCopied(true);
      setError(null);
    } catch {
      setError('Copying isn’t available. Select the dialogue and copy it.');
    }
  }
  return (
    <article
      className="npc-line"
      aria-label={`Dialogue for ${cue.npc_name}`}
      aria-busy={working}
    >
      <div className="npc-line__content">
        <p className="npc-line__eyebrow">{cue.npc_name} has something to say</p>
        <blockquote className="npc-line__text">{cue.line}</blockquote>
        <div className="npc-line__footer">
          <p className="npc-line__hint">Read aloud or make it yours.</p>
          <div className="npc-line__actions">
            <button
              type="button"
              className="button button--ghost button--small"
              onClick={() => void copy()}
            >
              {copied ? 'Copied' : 'Copy line'}
            </button>
            <button
              type="button"
              className="button button--quiet button--small"
              disabled={working}
              onClick={() => void resolve('dismissed')}
            >
              {working ? 'Dismissing…' : 'Dismiss'}
            </button>
          </div>
        </div>
        {error ? (
          <p className="notice notice--error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </article>
  );
}
